import api from './axios';
import { getStoredToken, rememberPostLoginRedirect, removeToken } from '../utils/authStorage';
import {
    ChatResponse, PromptSuggestion,
    Conversation, ConversationDetails, ConversationUpdate,
    FileUploadResponse, FileListItem, FileDeleteResponse,
    FeedbackType, FeedbackReasonCode, RefreshVariant
} from './types'

type StreamStatusEvent = {
    type: string;
    message: string;
    meta?: Record<string, unknown>;
};

type StreamFinalPayload = {
    response: any;
    conversation_id?: string;
};

type StreamCallbacks = {
    onStatus?: (event: StreamStatusEvent) => void;
    onToken?: (delta: string) => void;
    onFinal?: (payload: StreamFinalPayload) => void;
};

const buildStreamUrl = (path: string, business_id?: string) => {
    const baseUrl = (api.defaults.baseURL || '').replace(/\/$/, '');
    const url = new URL(`${baseUrl}${path}`, window.location.origin);
    if (business_id) {
        url.searchParams.set('business_id', business_id);
    }
    return url.toString();
};

const streamChatRequest = async (
    path: string,
    payload: Record<string, unknown>,
    callbacks: StreamCallbacks,
    business_id?: string,
    signal?: AbortSignal
): Promise<StreamFinalPayload> => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
    };
    const token = getStoredToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(buildStreamUrl(path, business_id), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal,
    });

    if (!response.ok || !response.body) {
        const errorText = await response.text();
        if (response.status === 401) {
            rememberPostLoginRedirect(window.location.pathname + window.location.search);
            removeToken();
            window.location.href = '/';
        }
        const error = new Error(errorText || `Streaming request failed with status ${response.status}`) as Error & { status?: number };
        error.status = response.status;
        throw error;
    }

    const reader = response.body.getReader();
    /** Without this, `reader.read()` can stay pending while the server is busy (no bytes), so Stop appears to do nothing. */
    const cancelReaderOnAbort = () => {
        reader.cancel().catch(() => { });
    };
    if (signal) {
        if (signal.aborted) {
            cancelReaderOnAbort();
        } else {
            signal.addEventListener('abort', cancelReaderOnAbort);
        }
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let finalPayload: StreamFinalPayload | null = null;

    const flushBlock = (block: string) => {
        const lines = block
            .split('\n')
            .map((line) => line.trimEnd())
            .filter(Boolean);
        if (!lines.length) {
            return;
        }

        let eventName = 'message';
        const dataLines: string[] = [];
        for (const line of lines) {
            if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
            }
        }

        if (!dataLines.length) {
            return;
        }

        const data = JSON.parse(dataLines.join('\n'));
        if (eventName === 'status') {
            callbacks.onStatus?.(data as StreamStatusEvent);
            return;
        }
        if (eventName === 'token') {
            callbacks.onToken?.(String((data as { delta?: string }).delta || ''));
            return;
        }
        if (eventName === 'final') {
            finalPayload = data as StreamFinalPayload;
            callbacks.onFinal?.(finalPayload);
            return;
        }
        if (eventName === 'error') {
            throw new Error(String((data as { message?: string }).message || 'Streaming request failed'));
        }
    };

    try {
        while (true) {
            if (signal?.aborted) {
                await reader.cancel().catch(() => { });
                throw new DOMException('Aborted', 'AbortError');
            }
            let readResult: ReadableStreamReadResult<Uint8Array>;
            try {
                readResult = await reader.read();
            } catch (e) {
                if (signal?.aborted) {
                    throw new DOMException('Aborted', 'AbortError');
                }
                throw e;
            }
            const { done, value } = readResult;
            buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
                const block = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                flushBlock(block);
                boundary = buffer.indexOf('\n\n');
            }

            if (done) {
                if (buffer.trim()) {
                    flushBlock(buffer.trim());
                }
                break;
            }
        }
    } finally {
        if (signal) {
            signal.removeEventListener('abort', cancelReaderOnAbort);
        }
    }

    if (!finalPayload) {
        throw new Error('Streaming completed without a final payload');
    }

    return finalPayload;
};

const chatApi = {
    // Ask a Question
    ask: async (
        question: string,
        conversation_id: string | null = null,
        business_id?: string
    ): Promise<ChatResponse> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post('/chat/ask', { question, conversation_id }, config);
            return response.data;
        } catch (error) {
            console.error("chatApi.ask failed:", error);
            throw error;
        }
    },

    // Ask a Question with Page Context
    askPage: async (
        question: string,
        page: string,
        year: number,
        month: number,
        conversation_id: string | null = null,
        business_id?: string
    ): Promise<ChatResponse> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post('/chat/ask-page', {
                question,
                conversation_id,
                page,
                year,
                month
            }, config);
            return response.data;
        } catch (error) {
            console.error("chatApi.askPage failed:", error);
            throw error;
        }
    },

    /** Backend feature flag: sheet edit proposals / apply when true; read-only Q&A when false. */
    getCfoAgentConfig: async (
        business_id?: string
    ): Promise<{ cfo_agent_writes_enabled: boolean }> => {
        const config = business_id ? { params: { business_id } } : {};
        const res = await api.get<{ cfo_agent_writes_enabled: boolean }>('/chat/cfo-agent-config', config);
        return res.data;
    },

    askStream: async (
        question: string,
        conversation_id: string | null = null,
        business_id?: string,
        callbacks: StreamCallbacks = {}
    ): Promise<StreamFinalPayload> => {
        return streamChatRequest(
            '/chat/ask-stream',
            { question, conversation_id },
            callbacks,
            business_id
        );
    },

    askPageStream: async (
        question: string,
        page: string,
        year: number,
        month: number,
        conversation_id: string | null = null,
        business_id?: string,
        callbacks: StreamCallbacks = {},
        sheetContext?: {
            google_sheet_source_document_id?: string;
            google_sheet_file_id?: string;
        },
        signalContext?: {
            signal_focus_id?: string;
            signal_focus_title?: string;
        },
        documentContext?: {
            focused_document_ids?: string[];
            focused_document_names?: string[];
        },
        signal?: AbortSignal
    ): Promise<StreamFinalPayload> => {
        const payload: Record<string, unknown> = {
            question,
            conversation_id,
            page,
            year,
            month,
        };
        if (sheetContext?.google_sheet_source_document_id) {
            payload.google_sheet_source_document_id = sheetContext.google_sheet_source_document_id;
        }
        if (sheetContext?.google_sheet_file_id) {
            payload.google_sheet_file_id = sheetContext.google_sheet_file_id;
        }
        if (signalContext?.signal_focus_id) {
            payload.signal_focus_id = signalContext.signal_focus_id;
        }
        if (signalContext?.signal_focus_title) {
            payload.signal_focus_title = signalContext.signal_focus_title;
        }
        const focusedIds = documentContext?.focused_document_ids;
        if (Array.isArray(focusedIds) && focusedIds.length > 0) {
            payload.focused_document_ids = focusedIds;
        }
        const focusedNames = documentContext?.focused_document_names;
        if (Array.isArray(focusedNames) && focusedNames.length > 0) {
            payload.focused_document_names = focusedNames;
        }
        return streamChatRequest('/chat/ask-page-stream', payload, callbacks, business_id, signal);
    },

    refreshAnswerStream: async (
        conversation_id: string,
        answer_message_id: string,
        question: string,
        page: string,
        year: number,
        month: number,
        variant: RefreshVariant,
        answer_meta?: Record<string, unknown>,
        business_id?: string,
        callbacks: StreamCallbacks = {}
    ): Promise<StreamFinalPayload> => {
        return streamChatRequest(
            '/chat/refresh-answer-stream',
            { conversation_id, answer_message_id, question, page, year, month, variant, answer_meta },
            callbacks,
            business_id
        );
    },

    submitAnswerFeedback: async (
        payload: {
            conversation_id: string;
            answer_message_id: string;
            feedback: FeedbackType;
            reason_code?: FeedbackReasonCode;
            reason_note?: string;
            page?: string;
            year?: number;
            month?: number;
            answer_meta?: Record<string, unknown>;
        },
        business_id?: string,
    ): Promise<{ status: string }> => {
        const config = business_id ? { params: { business_id } } : {};
        const response = await api.post('/chat/answer-feedback', payload, config);
        return response.data;
    },

    // Get Sample Prompts List
    getPrompts: async (): Promise<PromptSuggestion[]> => {
        try {
            const response = await api.get('/qbo/get-prompts');
            return response.data.sample_questions;
        } catch (error) {
            console.error("chatApi.getPrompts failed:", error);
            throw error;
        }
    },

    // Get List of Conversations
    listConversations: async (businessId?: string, search?: string): Promise<Conversation[]> => {
        try {
            const params: Record<string, string> = {};
            if (businessId) params.business_id = businessId;
            if (search && search.trim()) params.search = search.trim();
            const response = await api.get('/chat/conversations', { params });
            return response.data;
        } catch (error) {
            console.error("chatApi.listConversations failed:", error);
            throw error;
        }
    },

    // Get Messages for a Conversation
    getConversation: async (
        conversation_id: string,
        businessId?: string
    ): Promise<ConversationDetails> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get(`/chat/conversations/${conversation_id}`, { params });
            return response.data;
        } catch (error) {
            console.error("chatApi.getConversation failed:", error);
            throw error;
        }
    },

    // Update a Conversation
    updateConversation: async (
        conversation_id: string,
        update: ConversationUpdate,
        business_id?: string
    ): Promise<{ status: string; updated: ConversationUpdate }> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.patch(`/chat/conversations/${conversation_id}`, update, config);
            return response.data;
        } catch (error) {
            console.error("chatApi.updateConversation failed:", error);
            throw error;
        }
    },

    // Soft-delete a Conversation
    deleteConversation: async (
        conversation_id: string,
        business_id?: string
    ): Promise<{ status: string; updated: ConversationUpdate }> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.patch(
                `/chat/conversations/${conversation_id}`,
                { is_deleted: true },
                config
            );
            return response.data;
        } catch (error) {
            console.error("chatApi.deleteConversation failed:", error);
            throw error;
        }
    },

    // Upload a File
    uploadFile: async (
        file: File,
        conversation_id: string | null,
        business_id?: string
    ): Promise<FileUploadResponse> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (conversation_id) {
                formData.append('conversation_id', conversation_id);
            }

            const config: any = {
                headers: { 'Content-Type': 'multipart/form-data' },
            };
            if (business_id) {
                config.params = { business_id };
            }

            const response = await api.post('/chat/upload', formData, config);

            return response.data;
        } catch (error) {
            console.error("chatApi.uploadFile failed:", error);
            throw error;
        }
    },

    // Get Uploaded Files List
    getFiles: async (
        conversation_id?: string,
        business_id?: string
    ): Promise<FileListItem[]> => {
        try {
            const params: Record<string, string> = {};
            if (conversation_id) params.conversation_id = conversation_id;
            if (business_id) params.business_id = business_id;
            const response = await api.get('/chat/user_files', { params });
            return response.data;
        } catch (error) {
            console.error("chatApi.getFiles failed:", error);
            throw error;
        }
    },

    // Delete a File
    deleteFile: async (
        file_id: string,
        business_id?: string
    ): Promise<FileDeleteResponse> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.delete(`/chat/user_files/${file_id}`, config);
            return response.data;
        } catch (error) {
            console.error("chatApi.deleteFile failed:", error);
            throw error;
        }
    },
    // Get Page Summary
    calculatePageSummary: async (
        page: string,
        year: number,
        month: number,
        refresh: boolean = false,
        business_id?: string
    ): Promise<{ summary: string; is_cached: boolean }> => {
        try {
            const params: Record<string, any> = { page, year, month, refresh };
            if (business_id) params.business_id = business_id;
            const response = await api.get('/chat/page_summary', { params });
            return response.data;
        } catch (error) {
            console.error("chatApi.calculatePageSummary failed:", error);
            throw error;
        }
    },
};

export default chatApi;
